<template>
	<div v-if="readonly.length > 0" class="p-3 mb-4 rounded-md bg-gray-50 shadow-sm">
		<p class="text-start text-xs text-gray-500 mb-4">Saved images</p>

		<ul
			role="list"
			class="grid grid-cols-3 gap-x-4 gap-y-4 sm:grid-cols-4 sm:gap-x-6 xl:gap-x-8">
			<li
				v-for="(file, index) in readonly"
				:key="file.path"
				class="relative"
				data-testid="saved-file">
				<div
					class="group aspect-h-7 aspect-w-10 block w-full overflow-hidden rounded-md bg-gray-100"
					@click="viewing = index">
					<img
						:src="file.path ?? ''"
						:alt="`Preview ${index + 1}`"
						class="pointer-events-none object-cover" />
				</div>

				<p class="pointer-events-none mt-2 truncate block text-xs text-gray-500">
					{{ formatDate(String(file.created_at)) }}
				</p>
			</li>
		</ul>
	</div>

	<ul
		data-testid="file-list"
		role="list"
		class="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 xl:gap-x-8">
		<li
			v-for="(file, index) in editable"
			:key="index"
			class="relative"
			data-testid="file-preview">
			<app-icon
				v-if="selected.findIndex((element) => element == index + readonly.length) !== -1"
				:icon="['fas', 'fa-check-circle']"
				class="absolute top-2 left-2 z-20 text-white" />
			<div
				class="group aspect-h-7 aspect-w-10 block w-full overflow-hidden rounded-lg focus-within:ring-2 focus-within:ring-mabey-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 bg-gray-100"
				:class="
					selected.findIndex((element) => element == index + readonly.length) !== -1
						? 'ring-2 ring-mabey-600'
						: ''
				"
				@click="select(index + readonly.length)">
				<img
					:src="file.preview ?? ''"
					:alt="`Preview ${index + 1}`"
					class="pointer-events-none object-cover" />
			</div>

			<p
				class="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900"
				data-testid="file-name">
				{{ file.name }}
			</p>
			<p
				class="pointer-events-none block text-sm font-medium text-gray-500"
				data-testid="file-size">
				{{ fileSizeToHumanReadable(file.size) }}
			</p>
		</li>
	</ul>

	<div class="flex flex-col gap-3 mt-4 justify-stretch">
		<slot></slot>

		<app-button v-if="selected.length > 0" theme="danger" @click="remove">
			Remove {{ selected.length }} Image(s)
		</app-button>
	</div>

	<confirm
		:open="viewing !== null"
		title=""
		theme="info"
		button="Done"
		:cancel="false"
		@confirmed="viewing = null">
		<img
			v-if="viewing !== null"
			:src="readonly[viewing].path"
			:alt="`Saved image`"
			class="pointer-events-none block mx-auto" />
	</confirm>
</template>

<script lang="ts" setup>
	import { ref, computed } from 'vue'
	import { fileSizeToHumanReadable } from '@/utils/fileSize'
	import type { FileResponse } from '@/data/types/file-response'
	import type { PreviewFile } from '@/data/interfaces/preview-file'
	import { formatDate } from '@/utils/date'
	import Confirm from '@/components/partials/dialogs/Confirm.vue'

	const props = defineProps({
		files: {
			type: Array<PreviewFile | FileResponse>,
			default: []
		}
	})

	const selected = ref<number[]>([])
	const viewing = ref<number | null>(null)
	const emit = defineEmits(['remove'])

	const editable = computed(() => {
		return props.files.filter(isFile)
	})

	const readonly = computed(() => {
		return props.files.filter(isFileResponse)
	})

	function isFileResponse(item: PreviewFile | FileResponse): item is FileResponse {
		return (item as FileResponse).path !== undefined
	}

	function isFile(item: PreviewFile | FileResponse): item is PreviewFile {
		return (item as PreviewFile).lastModified !== undefined
	}

	function select(index: number) {
		const found = selected.value.findIndex((element) => element === index)

		if (found !== -1) {
			selected.value.splice(found, 1)
		} else {
			selected.value.push(index)
		}
	}

	function remove() {
		emit('remove', selected.value)
		selected.value = []
	}
</script>
